import { useState } from 'react';
import { PageLoader } from '@/components/ui/spinner';
import { Link, useLocation, useRoute } from 'wouter';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Plus,
  Upload,
  Users,
  FileText,
  ClipboardList,
  Receipt,
  UserMinus,
  MessageCircle,
  Bot,
  Paperclip,
  ShieldCheck,
  LogIn,
  Pencil,
  Landmark,
  Download,
} from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { api, ApiCallError } from '@/lib/api';
import { downloadHechauka } from '@/lib/downloads';
import { toNationalDisplay } from '@shared/phone';
import { useCurrentAdmin } from '@/hooks/use-current-admin';
import { AdminShell } from '@/components/admin-shell';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TenantStatusBadge } from '@/components/status-badge';
import { RucFormDialog } from '@/components/ruc-form-dialog';
import { FiscalProfileDialog } from '@/components/fiscal-profile-dialog';
import { SetAccessCard } from '@/components/set-access-card';
import { MarangatuSnapshotCard } from '@/components/marangatu-snapshot-card';
import { ComprobantesCard } from '@/components/comprobantes-card';
import { SyncFromSetButton } from '@/components/sync-set-button';
import { UploadInvoiceDialog } from '@/components/upload-invoice-dialog';
import { IvaDeclarationDialog } from '@/components/iva-declaration-dialog';
import { TenantEditDialog } from '@/components/tenant-edit-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface TenantDetail {
  tenant: {
    id: string;
    whatsappNumber: string;
    fullName: string;
    displayName: string;
    email: string | null;
    status: 'onboarding' | 'active' | 'paused' | 'cancelled';
    onboardedAt: string | null;
    createdAt: string;
  };
  rucs: Array<{
    id: string;
    ruc: string;
    razonSocial: string;
    regimen: 'iva_irp' | 'ire_simple';
    isPrimary: boolean;
    validatedAt: string | null;
    createdAt: string;
    // Perfil fiscal SET (poblado vía Marangatu). Todos opcionales.
    nombreFantasia: string | null;
    actividadEconomica: string | null;
    codigoActividad: string | null;
    domicilioFiscal: string | null;
    fechaInicioActividades: string | null;
    emailFiscal: string | null;
    telefonoFiscal: string | null;
    diaVencimientoIva: number | null;
    diaVencimientoIrp: number | null;
    representanteLegalRuc: string | null;
    representanteLegalNombre: string | null;
    marangatuSyncedAt: string | null;
  }>;
  subscription: {
    id: string;
    plan: 'esencial' | 'pro';
    planId: string | null;
    planName: string | null;
    planSlug: string | null;
    planPriceAmount: number | null;
    planCurrency: string | null;
    planBillingPeriod: string | null;
    billingCycle: 'monthly' | 'annual';
    status: 'trialing' | 'active' | 'past_due' | 'cancelled';
    currentPeriodEnd: string;
  } | null;
  assignments: Array<{
    adminId: string;
    email: string;
    fullName: string;
    role: 'accountant' | 'superadmin';
    assignedAt: string;
  }>;
  counts: {
    invoicesTotal: number;
    invoicesPending: number;
    declarationsTotal: number;
  };
}

interface Invoice {
  id: string;
  direction: 'purchase' | 'sale';
  invoiceNumber: string;
  issueDate: string;
  senderRazonSocial: string;
  currency: string;
  grandTotal: string;
  status: string;
  createdAt: string;
  // 'invoice' = fila de la tabla invoices (tiene detalle admin); 'electronic' =
  // factura emitida por Actuo (electronic_invoices), solo lectura acá.
  source?: 'invoice' | 'electronic';
}

interface AdminRow {
  id: string;
  email: string;
  fullName: string;
  role: 'accountant' | 'superadmin';
  isActive: boolean;
}

export function TenantDetailPage() {
  const [, navigate] = useLocation();
  const [, params] = useRoute('/admin/tenants/:id');
  const { data: me, isLoading: loadingMe } = useCurrentAdmin();
  const qc = useQueryClient();
  const [showRucDialog, setShowRucDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showIvaDialog, setShowIvaDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  const tenantId = params?.id ?? '';

  const { data, isLoading, isError } = useQuery({
    queryKey: ['tenant', tenantId],
    queryFn: () => api<TenantDetail>(`/api/admin/tenants/${tenantId}`),
    enabled: !!me && !!tenantId,
  });

  const statusMutation = useMutation({
    mutationFn: (status: TenantDetail['tenant']['status']) =>
      api(`/api/admin/tenants/${tenantId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant', tenantId] }),
  });

  const impersonateMutation = useMutation({
    mutationFn: () =>
      api(`/api/admin/tenants/${tenantId}/impersonate`, { method: 'POST' }),
    onSuccess: () => {
      toast.success('Entrando al panel del contacto…');
      navigate('/app');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (loadingMe) return <PageLoader />;
  if (!me) {
    navigate('/login');
    return null;
  }
  if (isLoading) return <AdminShell><div className="text-muted-foreground">Cargando contacto…</div></AdminShell>;
  if (isError || !data) {
    return (
      <AdminShell>
        <div className="text-destructive">No se pudo cargar el contacto.</div>
        <Button variant="link" asChild>
          <Link href="/admin/tenants">
            <ArrowLeft />
            Volver al listado
          </Link>
        </Button>
      </AdminShell>
    );
  }

  const { tenant, rucs, subscription, assignments, counts } = data;

  return (
    <AdminShell>
      {/* max-md:pl-9 despeja el trigger del sidebar (absoluto, top-left en mobile):
          esta página arranca con el link de volver, no con el h1. */}
      <div className="mb-4 max-md:pl-9">
        <Button variant="link" size="sm" asChild className="px-0">
          <Link href="/admin/tenants">
            <ArrowLeft />
            Tenants
          </Link>
        </Button>
      </div>

      <div className="rounded-lg border border-border bg-background shadow-sm p-4 mb-6 md:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-foreground">{tenant.fullName}</h1>
              <TenantStatusBadge status={tenant.status} />
            </div>
            {/* Identidad fiscal — debajo del nombre, como cédula en un perfil oficial */}
            {rucs[0] ? (
              <div className="mt-1 flex items-center gap-2 text-sm">
                <FileText className="size-3.5 text-muted-foreground" />
                <span className="font-mono text-foreground font-medium">{rucs[0].ruc}</span>
                <span className="text-muted-foreground">·</span>
                <span className="text-foreground">{rucs[0].razonSocial}</span>
                {rucs[0].validatedAt ? (
                  <Badge variant="success" className="ml-1 text-[10px] py-0 px-1.5">validado</Badge>
                ) : (
                  <Badge variant="warning" className="ml-1 text-[10px] py-0 px-1.5">pendiente SET</Badge>
                )}
              </div>
            ) : (
              <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground italic">
                <FileText className="size-3.5" />
                Sin RUC cargado — ver tab "RUC" para cargarlo
              </div>
            )}
            <div className="mt-2 grid grid-cols-1 gap-x-8 gap-y-1 text-sm text-muted-foreground sm:grid-cols-2 lg:grid-cols-3">
              <div>
                <span className="font-medium text-foreground">WhatsApp:</span>{' '}
                <span className="font-mono">{toNationalDisplay(tenant.whatsappNumber)}</span>
              </div>
              <div>
                <span className="font-medium text-foreground">Email:</span> {tenant.email ?? '—'}
              </div>
              <div>
                <span className="font-medium text-foreground">Trato:</span> {tenant.displayName}
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">Plan:</span>
                {subscription ? (
                  <Badge variant="default">
                    {subscription.planName ?? subscription.plan} · {subscription.billingCycle === 'monthly' ? 'mensual' : 'anual'}
                  </Badge>
                ) : (
                  <Badge variant="warning">sin plan</Badge>
                )}
              </div>
              <div>
                <span className="font-medium text-foreground">Creado:</span>{' '}
                {new Date(tenant.createdAt).toLocaleDateString('es-PY')}
              </div>
              <div>
                <span className="font-medium text-foreground">Onboarded:</span>{' '}
                {tenant.onboardedAt ? new Date(tenant.onboardedAt).toLocaleDateString('es-PY') : '—'}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 lg:shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEditDialog(true)}
            >
              <Pencil />
              Editar
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => impersonateMutation.mutate()}
              disabled={impersonateMutation.isPending}
            >
              <LogIn />
              Ver panel del contacto
            </Button>
            <Select
              value={tenant.status}
              onValueChange={(v) => statusMutation.mutate(v as TenantDetail['tenant']['status'])}
              disabled={statusMutation.isPending}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="onboarding">Onboarding</SelectItem>
                <SelectItem value="active">Activo</SelectItem>
                <SelectItem value="paused">Pausado</SelectItem>
                <SelectItem value="cancelled">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Stat label="Facturas" value={counts.invoicesTotal} sub={`${counts.invoicesPending} pendientes`} />
          <Stat label="Declaraciones" value={counts.declarationsTotal} />
          <Stat label="Contadores asignados" value={assignments.length} />
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-4 w-full justify-start overflow-x-auto md:overflow-visible md:w-auto [&>*]:shrink-0">
          <TabsTrigger value="overview">Resumen</TabsTrigger>
          <TabsTrigger value="rucs">
            <Landmark />
            SET
          </TabsTrigger>
          <TabsTrigger value="invoices">
            <Receipt />
            Facturas
          </TabsTrigger>
          <TabsTrigger value="declarations">
            <ClipboardList />
            Declaraciones
          </TabsTrigger>
          <TabsTrigger value="chat">
            <MessageCircle />
            Chat
          </TabsTrigger>
          <TabsTrigger value="accountants">
            <Users />
            Contadores
          </TabsTrigger>
        </TabsList>

        <div className="rounded-lg border border-border bg-background shadow-sm overflow-hidden">
          <TabsContent value="overview" className="mt-0 p-4 md:p-6">
            <OverviewTab data={data} />
          </TabsContent>
          <TabsContent value="rucs" className="mt-0 p-4 space-y-6 md:p-6">
            <RucsTab tenantId={tenantId} rucs={rucs} onAddRuc={() => setShowRucDialog(true)} />
            <MarangatuSnapshotCard tenantId={tenantId} />
            {/* Acceso (credenciales) al final: se configura una vez y casi no se
                vuelve a tocar; colapsado por defecto cuando ya está configurado. */}
            <SetAccessCard endpoint={`/api/admin/tenants/${tenantId}/set-access`} />
          </TabsContent>
          <TabsContent value="invoices" className="mt-0 p-4 md:p-6">
            <InvoicesTab tenantId={tenantId} onUpload={() => setShowUploadDialog(true)} />
          </TabsContent>
          <TabsContent value="declarations" className="mt-0 p-4 md:p-6">
            <DeclarationsTab tenantId={tenantId} onGenerateIva={() => setShowIvaDialog(true)} />
          </TabsContent>
          <TabsContent value="chat" className="mt-0 p-4 md:p-6">
            <ChatTab tenantId={tenantId} />
          </TabsContent>
          <TabsContent value="accountants" className="mt-0 p-4 md:p-6">
            <AccountantsTab tenantId={tenantId} assignments={assignments} />
          </TabsContent>
        </div>
      </Tabs>

      <TenantEditDialog
        tenantId={tenantId}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        initial={{
          fullName: tenant.fullName,
          displayName: tenant.displayName,
          email: tenant.email,
        }}
      />
      <RucFormDialog tenantId={tenantId} open={showRucDialog} onOpenChange={setShowRucDialog} />
      <UploadInvoiceDialog
        tenantId={tenantId}
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
      />
      <IvaDeclarationDialog
        tenantId={tenantId}
        rucs={rucs}
        open={showIvaDialog}
        onOpenChange={setShowIvaDialog}
      />
    </AdminShell>
  );
}

function Stat({ label, value, sub }: { label: string; value: number | string; sub?: string }) {
  return (
    <div className="rounded-md bg-muted px-4 py-3">
      <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-bold text-foreground mt-1">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function OverviewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium text-foreground text-right">{value || '—'}</dd>
    </div>
  );
}

function OverviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      <dl className="divide-y divide-border/60">{children}</dl>
    </div>
  );
}

function OverviewTab({ data }: { data: TenantDetail }) {
  const ruc = data.rucs[0];
  const fmtDate = (s: string | null | undefined) =>
    s ? new Date(s).toLocaleDateString('es-PY') : '—';
  const STATUS_LABEL: Record<string, string> = {
    onboarding: 'En onboarding',
    active: 'Activo',
    paused: 'Pausado',
    cancelled: 'Cancelado',
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Ficha fiscal del contribuyente (RUC primario). */}
      <OverviewSection title="Datos fiscales">
        {ruc ? (
          <>
            <OverviewRow label="RUC" value={<span className="font-mono">{ruc.ruc}</span>} />
            <OverviewRow label="Razón social" value={ruc.razonSocial} />
            {ruc.nombreFantasia && <OverviewRow label="Nombre fantasía" value={ruc.nombreFantasia} />}
            <OverviewRow label="Régimen" value={ruc.regimen === 'iva_irp' ? 'IVA + IRP' : 'IRE Simple'} />
            {ruc.actividadEconomica && (
              <OverviewRow label="Actividad" value={ruc.actividadEconomica} />
            )}
            {ruc.domicilioFiscal && <OverviewRow label="Domicilio fiscal" value={ruc.domicilioFiscal} />}
            <OverviewRow
              label="RUC validado"
              value={ruc.validatedAt ? fmtDate(ruc.validatedAt) : 'Sin validar'}
            />
          </>
        ) : (
          <p className="py-2 text-sm text-muted-foreground">Sin RUC cargado todavía.</p>
        )}
      </OverviewSection>

      {/* Cuenta y contacto. */}
      <OverviewSection title="Cuenta">
        <OverviewRow label="Estado" value={STATUS_LABEL[data.tenant.status] ?? data.tenant.status} />
        <OverviewRow
          label="Plan"
          value={
            data.subscription
              ? (data.subscription.planName ?? data.subscription.plan)
              : 'Sin plan'
          }
        />
        <OverviewRow label="WhatsApp" value={data.tenant.whatsappNumber} />
        <OverviewRow label="Email" value={data.tenant.email} />
        <OverviewRow label="Alta" value={fmtDate(data.tenant.createdAt)} />
        <OverviewRow label="Última sync SET" value={fmtDate(ruc?.marangatuSyncedAt)} />
      </OverviewSection>

      {/* Editor de plan: catálogo `plans` (mig 037). El admin asigna/cambia
          el plan del tenant — UPSERT de subscription en el endpoint. */}
      <SubscriptionPlanEditor
        tenantId={data.tenant.id}
        currentPlanId={data.subscription?.planId ?? null}
      />

      {/* Historial de pagos manuales del tenant (mig 038). */}
      <PaymentsCard tenantId={data.tenant.id} />

      {/* Actividad (resumen rápido). */}
      <OverviewSection title="Actividad">
        <OverviewRow label="Facturas" value={data.counts.invoicesTotal} />
        <OverviewRow label="Pendientes de revisión" value={data.counts.invoicesPending} />
        <OverviewRow label="Declaraciones" value={data.counts.declarationsTotal} />
      </OverviewSection>

      {/* Contadores asignados. */}
      <OverviewSection title="Contadores asignados">
        {data.assignments.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">Sin contador asignado todavía.</p>
        ) : (
          data.assignments.map((a) => (
            <OverviewRow
              key={a.adminId}
              label={a.fullName}
              value={<span className="text-xs text-muted-foreground">{a.email}</span>}
            />
          ))
        )}
      </OverviewSection>
    </div>
  );
}

function RucsTab({
  tenantId,
  rucs,
  onAddRuc,
}: {
  tenantId: string;
  rucs: TenantDetail['rucs'];
  onAddRuc: () => void;
}) {
  const qc = useQueryClient();
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const validateMutation = useMutation({
    mutationFn: (rucId: string) =>
      api<{ result: { setValid: boolean; unverified: boolean; error: string | null; razonSocial: string | null }; updated: boolean }>(
        `/api/admin/tenants/${tenantId}/rucs/${rucId}/validate`,
        { method: 'POST' }
      ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['tenant', tenantId] });
      if (r.result.setValid) {
        toast.success(`RUC validado contra SET${r.result.razonSocial ? ` — ${r.result.razonSocial}` : ''}`);
      } else if (r.result.unverified) {
        toast.warning('No se pudo consultar SET (endpoint no configurado o caído)');
      } else if (r.result.error === 'not_found') {
        toast.error('RUC no encontrado en SET');
      } else {
        toast.error(`Validación falló: ${r.result.error}`);
      }
    },
    onError: () => toast.error('No se pudo validar el RUC'),
  });

  const hasRuc = rucs.length > 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-foreground">RUC del contacto</h2>
        {!hasRuc && (
          <Button size="sm" onClick={onAddRuc}>
            <Plus />
            Cargar RUC
          </Button>
        )}
      </div>
      {!hasRuc ? (
        <p className="text-muted-foreground text-sm py-6 text-center">
          Este contacto todavía no tiene RUC cargado.
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>RUC</TableHead>
              <TableHead>Razón social</TableHead>
              <TableHead>Régimen</TableHead>
              <TableHead>Validado SET</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rucs.slice(0, 1).map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono whitespace-nowrap">{r.ruc}</TableCell>
                <TableCell className="max-w-[20rem]">
                  <div className="truncate" title={r.razonSocial}>
                    {r.razonSocial}
                  </div>
                </TableCell>
                <TableCell>{r.regimen === 'iva_irp' ? 'IVA + IRP' : 'IRE Simple'}</TableCell>
                <TableCell className="text-muted-foreground">
                  {r.validatedAt ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Badge variant="success">validado</Badge>
                      <span className="text-xs">
                        {new Date(r.validatedAt).toLocaleDateString('es-PY')}
                      </span>
                    </span>
                  ) : (
                    <Badge variant="warning">pendiente</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => validateMutation.mutate(r.id)}
                    disabled={validateMutation.isPending}
                  >
                    <ShieldCheck />
                    Validar SET
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Sección "Perfil fiscal SET" — solo se muestra si hay un RUC cargado.
          Lista los datos derivados de Marangatu con botón para editar. */}
      {hasRuc && rucs[0] && (
        <FiscalProfileSection
          ruc={rucs[0]}
          onEdit={() => setShowProfileDialog(true)}
        />
      )}

      {hasRuc && rucs[0] && (
        <FiscalProfileDialog
          tenantId={tenantId}
          rucId={rucs[0].id}
          ruc={rucs[0].ruc}
          razonSocial={rucs[0].razonSocial}
          regimen={rucs[0].regimen}
          initial={{
            nombreFantasia: rucs[0].nombreFantasia,
            actividadEconomica: rucs[0].actividadEconomica,
            codigoActividad: rucs[0].codigoActividad,
            domicilioFiscal: rucs[0].domicilioFiscal,
            fechaInicioActividades: rucs[0].fechaInicioActividades,
            emailFiscal: rucs[0].emailFiscal,
            telefonoFiscal: rucs[0].telefonoFiscal,
            diaVencimientoIva: rucs[0].diaVencimientoIva,
            diaVencimientoIrp: rucs[0].diaVencimientoIrp,
            representanteLegalRuc: rucs[0].representanteLegalRuc,
            representanteLegalNombre: rucs[0].representanteLegalNombre,
          }}
          open={showProfileDialog}
          onOpenChange={setShowProfileDialog}
        />
      )}
    </div>
  );
}

/**
 * Sección read-only que muestra los datos del perfil fiscal SET con
 * botón "Editar" que abre el dialog. Cada fila es label + valor; si
 * el campo está vacío muestra "—" en muted.
 */
function FiscalProfileSection({
  ruc,
  onEdit,
}: {
  ruc: TenantDetail['rucs'][number];
  onEdit: () => void;
}) {
  const rows: Array<[string, string | null | undefined, string?]> = [
    ['Nombre fantasía', ruc.nombreFantasia],
    ['Actividad económica', ruc.actividadEconomica],
    ['Código actividad', ruc.codigoActividad],
    ['Domicilio fiscal', ruc.domicilioFiscal],
    [
      'Inicio de actividades',
      ruc.fechaInicioActividades
        ? new Date(ruc.fechaInicioActividades + 'T12:00:00').toLocaleDateString('es-PY')
        : null,
    ],
    ['Email fiscal', ruc.emailFiscal],
    ['Teléfono fiscal', ruc.telefonoFiscal],
    [
      'Día venc. IVA',
      ruc.diaVencimientoIva ? `${ruc.diaVencimientoIva} de cada mes` : null,
    ],
    [
      'Día venc. IRP',
      ruc.diaVencimientoIrp ? `${ruc.diaVencimientoIrp}` : null,
    ],
    ['Rep. legal RUC', ruc.representanteLegalRuc],
    ['Rep. legal nombre', ruc.representanteLegalNombre],
  ];

  // Si TODOS los campos están vacíos, mostramos CTA grande "Cargar perfil".
  const allEmpty = rows.every(([, v]) => !v);
  const lastSync = ruc.marangatuSyncedAt;

  return (
    <div className="mt-6 border border-border rounded-md p-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground">
            Perfil fiscal SET
          </h3>
          <p className="text-xs text-muted-foreground">
            {lastSync
              ? `Sincronizado de Marangatu: ${new Date(lastSync).toLocaleDateString('es-PY')}`
              : 'Se completa solo al sincronizar Marangatu (o cargá a mano)'}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={onEdit}>
          <Pencil />
          {allEmpty ? 'Cargar a mano' : 'Editar'}
        </Button>
      </div>

      {allEmpty ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          Sin datos todavía. Sincronizá Marangatu (en "Comprobantes" más abajo) para
          completar el perfil fiscal automáticamente, o cargalo a mano.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          {rows.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-3">
              <span className="text-muted-foreground">{label}</span>
              <span className="text-right text-foreground">
                {value || <span className="text-muted-foreground/60">—</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InvoicesTab({ tenantId, onUpload }: { tenantId: string; onUpload: () => void }) {
  const [, navigate] = useLocation();
  // Filtro local por dirección (todos / egresos / ingresos) — el endpoint trae
  // las dos para evitar refetch al cambiar tab.
  const [dirFilter, setDirFilter] = useState<'all' | 'purchase' | 'sale'>('all');
  const { data, isLoading } = useQuery({
    queryKey: ['tenant', tenantId, 'invoices'],
    queryFn: () =>
      // pageSize=100 (cap del schema). El orden por issueDate desc deja arriba
      // las más recientes — suficiente para que las últimas ventas SET aparezcan.
      api<{ items: Invoice[]; total: number }>(`/api/admin/tenants/${tenantId}/invoices?pageSize=100`),
  });

  const allItems = data?.items ?? [];
  const filteredItems =
    dirFilter === 'all' ? allItems : allItems.filter((i) => i.direction === dirFilter);

  return (
    <div className="space-y-6">
      <ComprobantesCard tenantId={tenantId} />

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-foreground">Facturas</h2>
          <span className="text-xs text-muted-foreground">
            {filteredItems.length}{dirFilter !== 'all' && ` de ${allItems.length}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md border border-border p-0.5 text-xs">
            {(['all', 'purchase', 'sale'] as const).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDirFilter(d)}
                className={
                  'rounded-sm px-2 py-1 transition-colors ' +
                  (dirFilter === d ? 'bg-muted font-medium text-foreground' : 'text-muted-foreground hover:text-foreground')
                }
              >
                {d === 'all' ? 'Todas' : d === 'purchase' ? 'Egresos' : 'Ingresos'}
              </button>
            ))}
          </div>
          <Button size="sm" onClick={onUpload}>
            <Upload />
            Subir factura
          </Button>
        </div>
      </div>

      {isLoading && <p className="text-muted-foreground">Cargando…</p>}
      {!isLoading && filteredItems.length === 0 && (
        <p className="text-muted-foreground text-sm py-6 text-center">
          {dirFilter === 'sale'
            ? 'No hay ingresos en el listado. Si emitiste facturas en SET, sincronizá los comprobantes.'
            : dirFilter === 'purchase'
            ? 'No hay egresos en el listado.'
            : 'No hay facturas todavía. Cuando el contacto las mande por WhatsApp aparecen acá, o subí una manualmente con el botón de arriba.'}
        </p>
      )}
      {filteredItems.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Fecha</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Nº</TableHead>
              <TableHead>Contraparte</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead>Estado</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredItems.map((inv) => {
              const clickable = (inv.source ?? 'invoice') === 'invoice';
              return (
              <TableRow
                key={inv.id}
                className={clickable ? 'hover:bg-accent cursor-pointer' : ''}
                onClick={clickable ? () => navigate(`/admin/invoices/${inv.id}`) : undefined}
              >
                <TableCell className="text-muted-foreground whitespace-nowrap">
                  {new Date(inv.issueDate).toLocaleDateString('es-PY')}
                </TableCell>
                <TableCell>
                  <Badge variant={inv.direction === 'purchase' ? 'secondary' : 'success'}>
                    {inv.direction === 'purchase' ? 'Egreso' : 'Ingreso'}
                  </Badge>
                </TableCell>
                <TableCell className="font-mono text-xs">{inv.invoiceNumber}</TableCell>
                <TableCell className="max-w-[20rem]">
                  <div className="truncate" title={inv.senderRazonSocial}>
                    {inv.senderRazonSocial}
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono">
                  {inv.currency} {Number(inv.grandTotal).toLocaleString('es-PY')}
                </TableCell>
                <TableCell>
                  <Badge variant={invoiceTone(inv.status)}>{inv.status}</Badge>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function invoiceTone(s: string): 'secondary' | 'warning' | 'success' | 'destructive' {
  if (s === 'approved' || s === 'included_in_declaration') return 'success';
  if (s === 'rejected') return 'destructive';
  if (s === 'pending_review' || s === 'pending_extraction') return 'warning';
  return 'secondary';
}

function DeclarationsTab({
  tenantId,
  onGenerateIva,
}: {
  tenantId: string;
  onGenerateIva: () => void;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['tenant', tenantId, 'declarations'],
    queryFn: () =>
      api<{
        items: Array<{
          id: string;
          kind: string;
          formCode: string;
          periodYear: number;
          periodMonth: number | null;
          dueDate: string;
          status: string;
          netToPay: string;
          // 'actuo' = generada por Actuo (tabla declarations).
          // 'set'   = presentación histórica ingestada del portal SET.
          source?: 'actuo' | 'set';
          setReceipt?: string | null;
        }>;
      }>(`/api/admin/tenants/${tenantId}/declarations`),
  });

  if (isLoading) return <p className="text-muted-foreground">Cargando…</p>;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="text-base font-semibold text-foreground">Declaraciones</h2>
        <div className="flex flex-wrap items-center gap-2">
          <SyncFromSetButton
            tenantId={tenantId}
            label="Sincronizar desde SET"
            invalidateKeys={[['tenant', tenantId, 'declarations']]}
          />
          <Button size="sm" onClick={onGenerateIva}>
            <Plus />
            Generar IVA del mes
          </Button>
        </div>
      </div>
      {(!data || data.items.length === 0) && (
        <p className="text-muted-foreground text-sm py-6 text-center">
          No hay declaraciones generadas todavía. Cuando termine el período fiscal, generá la
          declaración IVA con el botón de arriba.
        </p>
      )}
      {data && data.items.length > 0 && (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Tipo</TableHead>
          <TableHead>Formulario</TableHead>
          <TableHead>Período</TableHead>
          <TableHead>Vence</TableHead>
          <TableHead className="text-right">A pagar</TableHead>
          <TableHead>Estado</TableHead>
          <TableHead className="text-right">Acciones</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.items.map((d) => (
          <TableRow key={d.id}>
            <TableCell>
              <div className="flex items-center gap-2">
                {d.kind.toUpperCase()}
                {d.source === 'set' && (
                  <Badge variant="outline" className="text-[10px]" title="Presentación importada del portal SET">
                    SET
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell className="font-mono text-xs">{d.formCode}</TableCell>
            <TableCell>
              {d.periodMonth ? `${d.periodMonth}/${d.periodYear}` : d.periodYear}
            </TableCell>
            <TableCell>{new Date(d.dueDate).toLocaleDateString('es-PY')}</TableCell>
            <TableCell className="text-right font-mono">
              {Number(d.netToPay).toLocaleString('es-PY')}
            </TableCell>
            <TableCell>
              <Badge
                variant={
                  d.status === 'submitted' || d.status === 'paid'
                    ? 'success'
                    : d.status === 'draft'
                    ? 'secondary'
                    : 'warning'
                }
              >
                {d.status}
              </Badge>
            </TableCell>
            <TableCell className="text-right">
              {/* Hechauka solo para declaraciones generadas por Actuo (F.120).
                  Las de SET (source='set') ya están presentadas → no aplica. */}
              {d.source !== 'set' && d.formCode === 'F.120' && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadHechauka(d.id)}
                  title="Descargar TXT para subir a Marangatu"
                >
                  <Download />
                  Hechauka
                </Button>
              )}
              {d.source === 'set' && d.setReceipt && (
                <span className="font-mono text-[10px] text-muted-foreground" title="Nº de documento SET">
                  {d.setReceipt}
                </span>
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
      )}
    </div>
  );
}

interface MessageRow {
  id: string;
  platform: 'whatsapp_baileys' | 'whatsapp_official' | 'telegram';
  direction: 'inbound' | 'outbound';
  kind: 'text' | 'image' | 'document' | 'audio' | 'video' | 'system';
  body: string | null;
  intent: 'invoice_upload' | 'query' | 'command' | 'unknown' | null;
  mediaFileId: string | null;
  createdAt: string;
}

function ChatTab({ tenantId }: { tenantId: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['tenant', tenantId, 'messages'],
    queryFn: () => api<{ items: MessageRow[] }>(`/api/admin/tenants/${tenantId}/messages?limit=100`),
    refetchInterval: 10_000,
  });

  if (isLoading) return <p className="text-muted-foreground">Cargando…</p>;
  if (!data || data.items.length === 0) {
    return (
      <p className="text-muted-foreground text-sm py-6 text-center">
        Sin conversación todavía. Cuando el cliente escriba al bot, los mensajes aparecen acá.
      </p>
    );
  }

  return (
    <div className="space-y-2 max-h-[600px] overflow-y-auto px-1">
      {data.items.map((m) => {
        const isInbound = m.direction === 'inbound';
        return (
          <div key={m.id} className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`}>
            <div
              className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${
                isInbound
                  ? 'bg-muted text-foreground'
                  : 'bg-primary text-white'
              }`}
            >
              <div className="text-[10px] uppercase tracking-wide opacity-70 flex items-center gap-1 mb-0.5">
                {isInbound ? (
                  <>Cliente</>
                ) : (
                  <>
                    <Bot className="size-3" />
                    Actuo
                  </>
                )}
                <span>·</span>
                <span>{m.platform.replace('whatsapp_', '').replace('_', ' ')}</span>
              </div>
              {m.kind === 'image' || m.kind === 'document' ? (
                <div className="flex items-center gap-2 italic opacity-90">
                  <Paperclip className="size-3.5" />
                  {m.kind === 'image' ? 'Imagen de factura' : 'Documento'}
                  {m.body && <span className="not-italic">— {m.body}</span>}
                </div>
              ) : (
                <p className="whitespace-pre-wrap">{m.body ?? '—'}</p>
              )}
              <div className="text-[10px] opacity-60 mt-1">
                {new Date(m.createdAt).toLocaleString('es-PY', {
                  day: '2-digit',
                  month: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function AccountantsTab({
  tenantId,
  assignments,
}: {
  tenantId: string;
  assignments: TenantDetail['assignments'];
}) {
  const qc = useQueryClient();
  const { data: adminsData } = useQuery({
    queryKey: ['admins'],
    queryFn: () => api<{ items: AdminRow[] }>('/api/admin/admins'),
  });

  const assignMutation = useMutation({
    mutationFn: (adminId: string) =>
      api(`/api/admin/tenants/${tenantId}/assignments`, {
        method: 'POST',
        body: JSON.stringify({ adminId }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant', tenantId] }),
  });

  const unassignMutation = useMutation({
    mutationFn: (adminId: string) =>
      api(`/api/admin/tenants/${tenantId}/assignments/${adminId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant', tenantId] }),
  });

  const assignedIds = new Set(assignments.map((a) => a.adminId));
  const available = (adminsData?.items ?? []).filter((a) => a.isActive && !assignedIds.has(a.id));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-foreground mb-1">Contadores responsables</h3>
        <p className="text-xs text-muted-foreground mb-3">
          Contadores del equipo Actuo que gestionan este cliente. Pueden revisar sus facturas y
          preparar sus declaraciones.
        </p>
        {assignments.length === 0 ? (
          <p className="text-muted-foreground text-sm">Sin contador asignado todavía.</p>
        ) : (
          <ul className="space-y-2">
            {assignments.map((a) => (
              <li
                key={a.adminId}
                className="flex items-center justify-between rounded-md border border-border px-3 py-2"
              >
                <div>
                  <div className="font-medium text-foreground text-sm">{a.fullName}</div>
                  <div className="text-xs text-muted-foreground">
                    {a.email} · {a.role}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => unassignMutation.mutate(a.adminId)}
                  disabled={unassignMutation.isPending}
                >
                  <UserMinus />
                  Quitar
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <h3 className="text-base font-semibold text-foreground mb-3">Asignar contador</h3>
        {available.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            Todos los contadores activos ya están asignados a este cliente.
          </p>
        ) : (
          <Select
            onValueChange={(v) => {
              if (v) assignMutation.mutate(v);
            }}
            disabled={assignMutation.isPending}
          >
            <SelectTrigger className="max-w-xs">
              <SelectValue placeholder="Elegí un contador…" />
            </SelectTrigger>
            <SelectContent>
              {available.map((a) => (
                <SelectItem key={a.id} value={a.id}>
                  {a.fullName} ({a.email})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────── Editor de plan del tenant ─────────────────────── */

interface PlanCatalogRow {
  id: string;
  name: string;
  slug: string;
  priceAmount: number;
  currency: string;
  billingPeriod: string;
  active: boolean;
  invoiceLimit: number | null;
}

function SubscriptionPlanEditor({
  tenantId,
  currentPlanId,
}: {
  tenantId: string;
  currentPlanId: string | null;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string>(currentPlanId ?? '');

  const { data: plansData, isLoading: loadingPlans } = useQuery({
    queryKey: ['admin-plans-active'],
    queryFn: () => api<{ items: PlanCatalogRow[] }>('/api/admin/plans'),
  });

  // Sincronizar el selected si cambia el current desde otra pestaña.
  // (Sin useEffect: solo update si el server-side cambió y el user no eligió otro.)
  if (currentPlanId && currentPlanId !== selected && selected === '') {
    setSelected(currentPlanId);
  }

  const setPlan = useMutation({
    mutationFn: (planId: string | null) =>
      api<{ ok: boolean }>(`/api/admin/tenants/${tenantId}/subscription/plan`, {
        method: 'PATCH',
        body: JSON.stringify({ planId }),
      }),
    onSuccess: () => {
      toast.success('Plan actualizado');
      qc.invalidateQueries({ queryKey: ['tenant-detail', tenantId] });
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiCallError ? (e.body.error ?? 'Error') : 'No se pudo actualizar el plan'
      ),
  });

  const plans = (plansData?.items ?? []).filter((p) => p.active);
  const hasChanged = (selected || null) !== (currentPlanId || null);

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 lg:col-span-2">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Plan asignado (catálogo)
      </h3>
      <p className="mb-3 text-xs text-muted-foreground">
        Elegí el plan del catálogo. El cambio se aplica al tenant inmediatamente
        (sin facturación automática todavía — el cobro lo registra el admin manualmente).
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Select value={selected || '__none__'} onValueChange={(v) => setSelected(v === '__none__' ? '' : v)} disabled={loadingPlans || setPlan.isPending}>
          <SelectTrigger className="w-72">
            <SelectValue placeholder="Sin plan asignado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">— Sin plan —</SelectItem>
            {plans.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.name} · {p.priceAmount.toLocaleString('es-PY')} {p.currency}/{p.billingPeriod === 'monthly' ? 'mes' : 'año'}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          onClick={() => setPlan.mutate(selected || null)}
          disabled={!hasChanged || setPlan.isPending}
        >
          {setPlan.isPending ? 'Guardando…' : 'Guardar plan'}
        </Button>
        {plans.length === 0 && !loadingPlans && (
          <Link href="/admin/plans" className="text-xs text-primary hover:underline">
            Crear planes en el catálogo →
          </Link>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────── Pagos manuales del tenant ─────────────────────── */

interface PaymentRow {
  id: string;
  amount: number;
  currency: string;
  method: 'transferencia' | 'efectivo' | 'tarjeta' | 'otro';
  reference: string | null;
  periodStart: string;
  periodEnd: string;
  paidAt: string;
  notes: string | null;
  recordedByEmail: string | null;
  createdAt: string;
}

const METHOD_LABEL: Record<string, string> = {
  transferencia: 'Transferencia',
  efectivo: 'Efectivo',
  tarjeta: 'Tarjeta',
  otro: 'Otro',
};

function fmtMoney(n: number, c = 'PYG'): string {
  return c === 'PYG'
    ? `Gs ${Math.round(n).toLocaleString('es-PY')}`
    : `${c} ${Math.round(n).toLocaleString('es-PY')}`;
}

function PaymentsCard({ tenantId }: { tenantId: string }) {
  const [openDialog, setOpenDialog] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ['tenant-payments', tenantId],
    queryFn: () => api<{ items: PaymentRow[] }>(`/api/admin/tenants/${tenantId}/payments`),
  });
  const items = data?.items ?? [];

  return (
    <div className="rounded-lg border border-border bg-muted/20 p-4 lg:col-span-2">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Pagos registrados
        </h3>
        <Button size="sm" onClick={() => setOpenDialog(true)}>
          <Plus /> Registrar pago
        </Button>
      </div>
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : items.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground text-center">
          Aún no hay pagos registrados.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border bg-background">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pagado</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead>Método</TableHead>
                <TableHead>Período</TableHead>
                <TableHead>Ref.</TableHead>
                <TableHead>Registrado por</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="whitespace-nowrap">
                    {new Date(p.paidAt).toLocaleDateString('es-PY')}
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums">
                    {fmtMoney(p.amount, p.currency)}
                  </TableCell>
                  <TableCell>{METHOD_LABEL[p.method] ?? p.method}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                    {p.periodStart} → {p.periodEnd}
                  </TableCell>
                  <TableCell className="text-xs">{p.reference || '—'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {p.recordedByEmail ?? '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <RecordPaymentDialog tenantId={tenantId} open={openDialog} onOpenChange={setOpenDialog} />
    </div>
  );
}

function RecordPaymentDialog({
  tenantId,
  open,
  onOpenChange,
}: {
  tenantId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);
  const monthLater = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  })();

  const [form, setForm] = useState({
    amount: '',
    currency: 'PYG',
    method: 'transferencia' as 'transferencia' | 'efectivo' | 'tarjeta' | 'otro',
    reference: '',
    periodStart: today,
    periodEnd: monthLater,
    paidAt: today,
    notes: '',
  });

  // Reset al abrir.
  if (!open && form.amount !== '') {
    setForm({
      amount: '', currency: 'PYG', method: 'transferencia',
      reference: '', periodStart: today, periodEnd: monthLater,
      paidAt: today, notes: '',
    });
  }

  const create = useMutation({
    mutationFn: () =>
      api(`/api/admin/tenants/${tenantId}/payments`, {
        method: 'POST',
        body: JSON.stringify({
          amount: Number(form.amount),
          currency: form.currency,
          method: form.method,
          reference: form.reference.trim() || null,
          periodStart: form.periodStart,
          periodEnd: form.periodEnd,
          paidAt: form.paidAt,
          notes: form.notes.trim() || null,
        }),
      }),
    onSuccess: () => {
      toast.success('Pago registrado');
      qc.invalidateQueries({ queryKey: ['tenant-payments', tenantId] });
      qc.invalidateQueries({ queryKey: ['tenant-detail', tenantId] });
      onOpenChange(false);
    },
    onError: (e) =>
      toast.error(
        e instanceof ApiCallError ? (e.body.error ?? 'Error') : 'No se pudo registrar el pago'
      ),
  });

  const canSubmit = Number(form.amount) > 0 && form.periodStart && form.periodEnd && form.paidAt;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[95vw] max-w-2xl overflow-x-hidden overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar pago</DialogTitle>
          <DialogDescription>
            Registro manual del pago recibido. Si el período del pago va más allá del
            actual, la subscription se extiende automáticamente.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Monto</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                placeholder="0"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Moneda</Label>
              <Input
                value={form.currency}
                onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
                maxLength={5}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Método</Label>
            <Select
              value={form.method}
              onValueChange={(v) => setForm({ ...form, method: v as typeof form.method })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="transferencia">Transferencia</SelectItem>
                <SelectItem value="efectivo">Efectivo</SelectItem>
                <SelectItem value="tarjeta">Tarjeta</SelectItem>
                <SelectItem value="otro">Otro</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Referencia (opcional)</Label>
            <Input
              value={form.reference}
              onChange={(e) => setForm({ ...form, reference: e.target.value })}
              placeholder="Nº comprobante, transferencia, etc."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Período desde</Label>
              <Input
                type="date"
                value={form.periodStart}
                onChange={(e) => setForm({ ...form, periodStart: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Período hasta</Label>
              <Input
                type="date"
                value={form.periodEnd}
                onChange={(e) => setForm({ ...form, periodEnd: e.target.value })}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Fecha de pago</Label>
            <Input
              type="date"
              value={form.paidAt}
              onChange={(e) => setForm({ ...form, paidAt: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Notas (opcional)</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              placeholder="Notas internas del pago"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={create.isPending}>
            Cancelar
          </Button>
          <Button onClick={() => create.mutate()} disabled={!canSubmit || create.isPending}>
            {create.isPending ? 'Guardando…' : 'Registrar pago'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
